// MomentScience Settings Management
export interface MomentSettings {
  // Basic Settings
  autoShow: boolean
  autoLoad: boolean
  dev: boolean
  
  // Appearance Settings
  ad_position: string
  darken_bg: boolean
  darken_bg_non_centered: boolean
  delay: number
  screen_margin: number
  
  // Unit Settings
  multi_offer_unit: boolean
  show_close: boolean
  enable_close_delay: boolean
  close_delay: number
  ad_animation: string
  close_ad_on_click_outside: boolean
  show_ads_on_exit: boolean
  
  // Button Settings
  button_order: string
  enable_effect_shimmer_pos_cta: boolean
  
  // Embedded Settings
  embedded: {
    enabled: boolean
    fill_container: boolean
    showBorder: boolean
    showHeader: boolean
    showFooter: boolean
    targetElement: string
  }
  
  // Styles
  styles: {
    header: {
      background: string
      text: string
      textColor: string
      lead_in_text: string
      lead_in_text_color: string
    }
    offerText: {
      font: string
      textColor: string
      buttonYes: {
        background: string
        color: string
        hover: string
      }
      buttonNo: {
        background: string
        color: string
        hover: string
        stroke: string
      }
    }
  }
}

export const defaultSettings: MomentSettings = {
  // Basic Settings
  autoShow: true,
  autoLoad: true,
  dev: true,
  
  // Appearance Settings
  ad_position: 'center',
  darken_bg: true,
  darken_bg_non_centered: false,
  delay: 0,
  screen_margin: 20,
  
  // Unit Settings
  multi_offer_unit: false,
  show_close: true,
  enable_close_delay: false,
  close_delay: 3,
  ad_animation: 'top-bottom',
  close_ad_on_click_outside: false,
  show_ads_on_exit: false,
  
  // Button Settings
  button_order: 'pos_first',
  enable_effect_shimmer_pos_cta: false,
  
  // Embedded Settings
  embedded: {
    enabled: false,
    fill_container: false,
    showBorder: true,
    showHeader: true,
    showFooter: true,
    targetElement: 'adpx'
  },
  
  // Styles
  styles: {
    header: {
      background: '#FE4215',
      text: 'Special Offer',
      textColor: '#FFFFFF',
      lead_in_text: 'Just for you',
      lead_in_text_color: '#FFFFFF'
    },
    offerText: {
      font: 'Inter, sans-serif',
      textColor: '#1A2B49',
      buttonYes: {
        background: '#FE4215',
        color: '#FFFFFF',
        hover: '#E53910'
      },
      buttonNo: {
        background: 'transparent',
        color: '#6E6D7A',
        hover: '#F3F3F3',
        stroke: '#6E6D7A'
      }
    }
  }
}

const STORAGE_KEY = 'moment-settings'

export const saveMomentSettings = (settings: MomentSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    console.log('Moment settings saved:', settings)
  } catch (error) {
    console.error('Failed to save moment settings:', error)
  }
}

export const loadMomentSettings = (): MomentSettings => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      // Merge with defaults to ensure all properties exist
      return { ...defaultSettings, ...parsed }
    }
  } catch (error) {
    console.error('Failed to load moment settings:', error)
  }
  return defaultSettings
}

export const generateMomentSDKConfig = (settings: MomentSettings) => {
  return {
    accountId: 'ffa59da09972e55e',
    autoShow: settings.autoShow,
    autoLoad: settings.autoLoad,
    dev: settings.dev,
    settings: {
      ad_position: settings.ad_position,
      darken_bg: settings.darken_bg,
      darken_bg_non_centered: settings.darken_bg_non_centered,
      delay: settings.delay,
      screen_margin: settings.screen_margin,
      multi_offer_unit: settings.multi_offer_unit,
      show_close: settings.show_close,
      enable_close_delay: settings.enable_close_delay,
      close_delay: settings.close_delay,
      ad_animation: settings.ad_animation,
      close_ad_on_click_outside: settings.close_ad_on_click_outside,
      show_ads_on_exit: settings.show_ads_on_exit,
      button_order: settings.button_order,
      enable_effect_shimmer_pos_cta: settings.enable_effect_shimmer_pos_cta,
      embedded: {
        enabled: settings.embedded.enabled,
        fill_container: settings.embedded.fill_container,
        showBorder: settings.embedded.showBorder,
        showHeader: settings.embedded.showHeader,
        showFooter: settings.embedded.showFooter,
        targetElement: settings.embedded.targetElement
      }
    },
    styles: {
      header: {
        background: settings.styles.header.background,
        text: settings.styles.header.text,
        textColor: settings.styles.header.textColor,
        lead_in_text: settings.styles.header.lead_in_text,
        lead_in_text_color: settings.styles.header.lead_in_text_color
      },
      offerText: {
        font: settings.styles.offerText.font,
        textColor: settings.styles.offerText.textColor,
        buttonYes: {
          background: settings.styles.offerText.buttonYes.background,
          color: settings.styles.offerText.buttonYes.color,
          hover: settings.styles.offerText.buttonYes.hover
        },
        buttonNo: {
          background: settings.styles.offerText.buttonNo.background,
          color: settings.styles.offerText.buttonNo.color,
          hover: settings.styles.offerText.buttonNo.hover,
          stroke: settings.styles.offerText.buttonNo.stroke
        }
      }
    }
  }
}

export const generateMomentSDKCode = (settings: MomentSettings): string => {
  const config = generateMomentSDKConfig(settings)
  return `// MomentScience SDK Configuration
window.AdpxConfig = ${JSON.stringify(config, null, 2)};

// User context for personalization
window.AdpxUser = {
  placement: 'post_transaction',
  theme_id: 'moment_post_transaction',
  product_id: 'your_product_id',
  order_value: 199.99,
  currency: 'USD',
  country: 'US'
};

// Load and initialize the SDK
(function() {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = 'https://cdn.pubtailer.com/launcher.min.js';
  script.crossOrigin = 'anonymous';
  script.async = true;
  script.onload = function() {
    if (window.Adpx) {
      window.Adpx.init(window.AdpxConfig);
    }
  };
  document.head.appendChild(script);
})();`
}
